import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPaymentMode, setFilterPaymentMode] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [formData, setFormData] = useState({
    category_id: '',
    amount: '',
    description: '',
    payment_mode: 'cash',
    reference: '',
    date: new Date().toISOString().split('T')[0]
  });

  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [editingCategory, setEditingCategory] = useState(null);

  const paymentModes = [
    { value: 'cash', label: 'Cash' },
    { value: 'bank', label: 'Bank Transfer' },
    { value: 'upi', label: 'UPI' },
    { value: 'card', label: 'Card' }
  ];

  useEffect(() => {
    fetchCategories();
    fetchExpenses();
    fetchSummary();
    // Set default date filters to yesterday and today
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    setFromDate(yesterday.toISOString().split('T')[0]);
    setToDate(today.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [filterCategory, filterPaymentMode, fromDate, toDate]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/expense-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchExpenses = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const params = {};
      if (filterCategory) params.category_id = filterCategory;
      if (filterPaymentMode) params.payment_mode = filterPaymentMode;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const response = await axios.get(`${API}/expenses`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setExpenses(response.data);
    } catch (err) {
      console.error('Error fetching expenses:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/expenses/summary/monthly`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return; // Prevent double submission
    setError('');
    setSaving(true);

    try {
      const token = localStorage.getItem('pos_token');
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        category_id: formData.category_id || null
      };

      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Expense updated successfully');
      } else {
        await axios.post(`${API}/expenses`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Expense added successfully');
      }

      setShowModal(false);
      setEditingExpense(null);
      resetForm();
      fetchExpenses();
      fetchSummary();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      category_id: expense.category_id || '',
      amount: expense.amount.toString(),
      description: expense.description,
      payment_mode: expense.payment_mode,
      reference: expense.reference || '',
      date: expense.date?.split('T')[0] || new Date().toISOString().split('T')[0]
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;

    try {
      const token = localStorage.getItem('pos_token');
      await axios.delete(`${API}/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Expense deleted successfully');
      fetchExpenses();
      fetchSummary();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete expense');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name) {
      setError('Category name is required');
      return;
    }

    try {
      const token = localStorage.getItem('pos_token');
      
      if (editingCategory) {
        // Update existing category
        await axios.put(`${API}/expense-categories/${editingCategory.id}`, newCategory, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Category updated successfully');
      } else {
        // Create new category
        await axios.post(`${API}/expense-categories`, newCategory, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Category added successfully');
      }
      
      setEditingCategory(null);
      setNewCategory({ name: '', description: '' });
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategory({ name: category.name, description: category.description || '' });
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const token = localStorage.getItem('pos_token');
      await axios.delete(`${API}/expense-categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Category deleted successfully');
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  const cancelEditCategory = () => {
    setEditingCategory(null);
    setNewCategory({ name: '', description: '' });
  };

  const seedDefaultCategories = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.post(`${API}/expense-categories/seed-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(response.data.message);
      fetchCategories();
    } catch (err) {
      setError('Failed to seed categories');
    }
  };

  const resetForm = () => {
    setFormData({
      category_id: '',
      amount: '',
      description: '',
      payment_mode: 'cash',
      reference: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toFixed(2)}`;

  // 80mm Thermal Print for Expenses Report (Head-wise)
  const handleExpensesThermalPrint = () => {
    if (!expenses.length) return;
    const totalAmount = expenses.reduce((s, e) => s + (e.amount || 0), 0);
    const now = new Date();
    const printDate = now.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const printTime = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    const fDate = fromDate || 'All';
    const tDate = toDate || 'All';

    const rows = expenses.map((exp, i) => {
      const expDate = new Date(exp.date);
      const d = expDate.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const t = expDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
      const notes = exp.description || '';
      return `<tr>
        <td>${i+1}</td>
        <td>${d} ${t}</td>
        <td>${exp.category_name || 'Uncategorized'}</td>
        <td class="r">${(exp.amount || 0).toFixed(2)}</td>
      </tr>${notes ? `<tr><td></td><td colspan="3" style="font-size:7px;color:#333;padding:0 1px 3px;">${notes}</td></tr>` : ''}`;
    }).join('');

    const printWindow = window.open('', '_blank', 'width=320,height=700');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Expenses Report</title>
    <style>
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family: 'Courier New', monospace; width:80mm; padding:3mm; color:#000; font-weight:bold; }
      .center { text-align:center; }
      .sep { border-top:1px dashed #000; margin:4px 0; }
      table { width:100%; border-collapse:collapse; font-size:9px; }
      th { text-align:left; padding:2px 1px; border-bottom:1px solid #000; font-weight:900; font-size:8px; }
      td { padding:2px 1px; font-size:9px; vertical-align:top; }
      td.r, th.r { text-align:right; }
      .header { font-size:12px; font-weight:900; margin-bottom:2px; }
      .sub { font-size:9px; margin-bottom:2px; }
    </style></head><body>
      <div class="center header">EXPENSES REPORT</div>
      <div class="center sub">From: ${fDate} To: ${tDate}</div>
      <div class="center sub">Printed: ${printDate} ${printTime}</div>
      <div class="sep"></div>
      <table>
        <thead><tr><th>Sl</th><th>Date & Time</th><th>Expense Head</th><th class="r">Amount</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="sep"></div>
      <div style="display:flex;justify-content:space-between;font-size:11px;font-weight:900;">
        <span>TOTAL EXPENSES</span><span>Rs. ${totalAmount.toFixed(2)}</span>
      </div>
      <div class="sep"></div>
      <div class="center sub" style="margin-top:4px;">--- End of Report ---</div>
    </body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 400);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="expenses-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Expenses</h1>
          <p className="text-slate-500">Track and manage business expenses</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExpensesThermalPrint}
            disabled={expenses.length === 0}
            className="px-4 py-2 bg-slate-800 text-white rounded-xl hover:bg-slate-900 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            data-testid="print-expenses-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            80mm Print
          </button>
          <button
            onClick={() => setShowCategoryModal(true)}
            className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors text-sm font-medium"
            data-testid="manage-categories-btn"
          >
            Categories
          </button>
          <button
            onClick={() => { setShowModal(true); setEditingExpense(null); resetForm(); }}
            className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-6 py-2 rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-2"
            data-testid="add-expense-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Expense
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
          {success}
        </div>
      )}

      {/* Monthly Summary */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500">This Month</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{formatCurrency(summary.total_expenses)}</p>
            <p className="text-xs text-slate-400 mt-1">{summary.expense_count} expenses</p>
          </div>

          {summary.by_payment_mode && Object.entries(summary.by_payment_mode).slice(0, 3).map(([mode, amount]) => (
            <div key={mode} className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-sm text-slate-500 capitalize">{mode}</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{formatCurrency(amount)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Category Breakdown */}
      {summary?.by_category?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="font-semibold text-slate-800 mb-4">Category Breakdown</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {summary.by_category.map((cat, idx) => (
              <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600 truncate">{cat.category_name}</p>
                <p className="text-lg font-bold text-slate-800">{formatCurrency(cat.amount)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            data-testid="filter-category"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          <select
            value={filterPaymentMode}
            onChange={(e) => setFilterPaymentMode(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            data-testid="filter-payment-mode"
          >
            <option value="">All Payment Modes</option>
            {paymentModes.map(pm => (
              <option key={pm.value} value={pm.value}>{pm.label}</option>
            ))}
          </select>

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            data-testid="from-date"
          />

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            data-testid="to-date"
          />
        </div>
      </div>

      {/* Expenses List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Category</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Description</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Payment</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Amount</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    No expenses found. Add your first expense!
                  </td>
                </tr>
              ) : (
                expenses.map(expense => {
                  // Check if expense can be edited (only today's expenses)
                  const expenseDate = new Date(expense.date).toISOString().split('T')[0];
                  const today = new Date().toISOString().split('T')[0];
                  const canEdit = expenseDate === today;
                  
                  return (
                    <tr key={expense.id} className="hover:bg-slate-50" data-testid={`expense-${expense.id}`}>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {new Date(expense.date).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
                          {expense.category_name || 'Uncategorized'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-800">{expense.description}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                          expense.payment_mode === 'cash' ? 'bg-green-100 text-green-700' :
                          expense.payment_mode === 'bank' ? 'bg-blue-100 text-blue-700' :
                          expense.payment_mode === 'upi' ? 'bg-purple-100 text-purple-700' :
                          'bg-slate-200 text-slate-700'
                        }`}>
                          {expense.payment_mode}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-red-600 text-right">
                        {formatCurrency(expense.amount)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleEdit(expense)}
                          disabled={!canEdit}
                          className={`mr-3 text-sm ${canEdit ? 'text-violet-600 hover:text-violet-700' : 'text-slate-300 cursor-not-allowed'}`}
                          data-testid={`edit-${expense.id}`}
                          title={!canEdit ? 'Cannot edit past expenses' : 'Edit expense'}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          disabled={!canEdit}
                          className={`text-sm ${canEdit ? 'text-red-600 hover:text-red-700' : 'text-slate-300 cursor-not-allowed'}`}
                          data-testid={`delete-${expense.id}`}
                          title={!canEdit ? 'Cannot delete past expenses' : 'Delete expense'}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Expense Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold">
                {editingExpense ? 'Edit Expense' : 'Add Expense'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                    data-testid="expense-amount-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                    data-testid="expense-date-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  placeholder="What was this expense for?"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="expense-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                    data-testid="expense-category-select"
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode</label>
                  <select
                    value={formData.payment_mode}
                    onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                    data-testid="expense-payment-mode-select"
                  >
                    {paymentModes.map(pm => (
                      <option key={pm.value} value={pm.value}>{pm.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reference</label>
                <input
                  type="text"
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  placeholder="Bank transaction ID, etc."
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  data-testid="expense-reference-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-violet-600 text-white px-4 py-2 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-expense-btn"
                >
                  {saving ? 'Saving...' : editingExpense ? 'Update' : 'Add'} Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Expense Categories</h3>
              <button onClick={() => { setShowCategoryModal(false); cancelEditCategory(); }} className="text-slate-400 hover:text-slate-600 text-2xl">×</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {categories.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500 mb-4">No categories yet</p>
                  <button
                    onClick={seedDefaultCategories}
                    className="px-4 py-2 bg-violet-100 text-violet-700 rounded-lg hover:bg-violet-200"
                    data-testid="seed-categories-btn"
                  >
                    Add Default Categories
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {categories.map(cat => (
                    <div key={cat.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg group" data-testid={`category-${cat.id}`}>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{cat.name}</p>
                        {cat.description && <p className="text-sm text-slate-500">{cat.description}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-400">{cat.expense_count || 0} expenses</span>
                        <button
                          onClick={() => handleEditCategory(cat)}
                          className="p-1.5 text-violet-600 hover:bg-violet-100 rounded transition-colors"
                          data-testid={`edit-category-${cat.id}`}
                          title="Edit category"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(cat.id)}
                          className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                          data-testid={`delete-category-${cat.id}`}
                          title="Delete category"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-200">
              <p className="text-sm text-slate-600 mb-2">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </p>
              <div className="space-y-2">
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="Category name"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="new-category-name-input"
                />
                <input
                  type="text"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="new-category-description-input"
                />
                <div className="flex gap-2">
                  {editingCategory && (
                    <button
                      onClick={cancelEditCategory}
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm"
                      data-testid="cancel-edit-category-btn"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    onClick={handleAddCategory}
                    className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm"
                    data-testid="add-category-btn"
                  >
                    {editingCategory ? 'Update' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
